"use client";

/**
 * <ConsentProvider> — 동의 상태 전역 관리 (Phase 0).
 *
 * 책임:
 *   1. mount 시 localStorage + (로그인이면) 서버에서 동의 상태 로드
 *   2. needs === true → ConsentBanner 표시 트리거
 *   3. update() 호출 시 → localStorage + 서버 동시 sync
 *
 * 분석 SDK (lib/analytics.ts) 는 hasAnalyticsConsent() 로 직접 localStorage 게이트.
 * Context 는 UI 렌더링 + 사용자 액션에 한정.
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { getAdvertisingConsent, setAdvertisingConsent, captureAdvertisingVisit, type AdvertisingConsent } from "@/lib/bite-advertising";
import { clearBiteAdvertisingEntry } from '@/lib/bite-entry';
import {
  saveLocalConsent,
  syncConsentToServer,
  initializeConsent,
  defaultConsent,
  type ConsentState,
} from "@/lib/consent";

interface ConsentContextValue {
  consent: ConsentState;
  advertising: AdvertisingConsent | null;
  /** 한 번도 동의한 적 없으면 true → 배너 표시 */
  needsBanner: boolean;
  /** 사용자가 모달을 명시적으로 열었는지 (재설정 용) */
  modalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  /**
   * 동의 갱신 — localStorage 즉시 + 로그인이면 서버 sync.
   * acceptAll / rejectOptional / 세부 toggle 모두 이 함수로.
   */
  update: (updates: { analytics?: boolean; marketing?: boolean; advertising?: boolean }) => Promise<void>;
  /** 모두 동의 shortcut */
  acceptAll: () => Promise<void>;
  /** 필수만 (선택 카테고리 모두 거부) shortcut */
  rejectOptional: () => Promise<void>;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children, advertisingAvailable = false }: { children: ReactNode; advertisingAvailable?: boolean }) {
  const [consent, setConsent] = useState<ConsentState>(defaultConsent());
  const [needsBanner, setNeedsBanner] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [advertising, setAdvertising] = useState<AdvertisingConsent | null>(null);
  const choiceVersion = useRef(0);
  const advertisingChoiceVersion = useRef(0);

  useEffect(() => {
    if (!advertisingAvailable) return;
    let cancelled = false;
    const version = advertisingChoiceVersion.current;
    void getAdvertisingConsent().then(async value => {
      if (cancelled || version !== advertisingChoiceVersion.current) return;
      if (value.decided && !value.enabled) clearBiteAdvertisingEntry();
      setAdvertising(value);
      if (value.enabled) await captureAdvertisingVisit();
    }).catch(() => { /* No advertising data is sent when consent cannot be verified. */ });
    return () => { cancelled = true; };
  }, [advertisingAvailable]);

  // 초기 로드 — localStorage + 서버 (로그인 사용자 한정)
  useEffect(() => {
    let cancelled = false;
    const version = choiceVersion.current;
    (async () => {
      const restored = await initializeConsent();
      if (cancelled || version !== choiceVersion.current) return;
      setConsent(restored.consent);
      setNeedsBanner(restored.needsBanner);
      setHydrated(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const update = useCallback(
    async (updates: { analytics?: boolean; marketing?: boolean; advertising?: boolean }) => {
      if (advertisingAvailable && updates.advertising !== undefined) {
        advertisingChoiceVersion.current++;
        const current = advertising || await getAdvertisingConsent();
        setAdvertising(await setAdvertisingConsent(updates.advertising, current.version));
      }
      choiceVersion.current++;
      const next = saveLocalConsent(updates);
      setConsent(next);
      setNeedsBanner(false);
      setHydrated(true);
      // 서버에도 sync (로그인 사용자만 — 비로그인은 401 무시)
      await syncConsentToServer({
        analytics: next.analytics,
        marketing: next.marketing,
      });
    },
    [advertisingAvailable, advertising]
  );

  const acceptAll = useCallback(async () => {
    const current = advertising || (advertisingAvailable ? await getAdvertisingConsent() : null);
    await update({ analytics: true, marketing: true, ...(current?.available ? { advertising: true } : {}) });
  }, [update, advertising, advertisingAvailable]);

  const rejectOptional = useCallback(async () => {
    await update({ analytics: false, marketing: false, ...(advertisingAvailable ? { advertising: false } : {}) });
  }, [update, advertisingAvailable]);

  const value: ConsentContextValue = {
    consent,
    advertising,
    needsBanner: hydrated ? needsBanner || Boolean(advertising?.available && !advertising.decided) : false,
    modalOpen,
    openModal: () => setModalOpen(true),
    closeModal: () => setModalOpen(false),
    update,
    acceptAll,
    rejectOptional,
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used inside <ConsentProvider>");
  }
  return ctx;
}
